/**
 * Alert Context
 *
 * Provides a global alert system that can be used from any component.
 * Replaces native alert() with styled AlertModal.
 */

import * as React from "react";
import { AlertModal, type AlertType } from "../components/ui/AlertModal";

interface AlertState {
  message: string;
  type: AlertType;
  callback?: () => void;
}

interface AlertContextValue {
  /**
   * Show an alert modal.
   * @param message - Message to display
   * @param type - Type of alert (error, success, info, warning)
   * @param callback - Optional callback when modal is closed
   */
  showAlert: (message: string, type?: AlertType, callback?: () => void) => void;
}

const AlertContext = React.createContext<AlertContextValue | null>(null);

interface AlertProviderProps {
  children: React.ReactNode;
}

export function AlertProvider({ children }: AlertProviderProps) {
  const [alert, setAlert] = React.useState<AlertState | null>(null);

  const showAlert = React.useCallback(
    (message: string, type: AlertType = "info", callback?: () => void) => {
      setAlert({ message, type, callback });
    },
    []
  );

  const handleClose = React.useCallback(() => {
    const cb = alert?.callback;
    setAlert(null);
    cb?.();
  }, [alert]);

  // Register global showAlert on mount
  React.useEffect(() => {
    setGlobalShowAlert(showAlert);
    return () => setGlobalShowAlert(null);
  }, [showAlert]);

  const contextValue = React.useMemo(() => ({ showAlert }), [showAlert]);

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
      <AlertModal
        open={alert !== null}
        message={alert?.message ?? ""}
        type={alert?.type ?? "info"}
        onClose={handleClose}
      />
    </AlertContext.Provider>
  );
}

/**
 * Hook to show alerts from any component.
 *
 * @example
 * const { showAlert } = useAlert();
 * showAlert("Ошибка сохранения", "error");
 * showAlert("Сохранено!", "success");
 */
export function useAlert(): AlertContextValue {
  const context = React.useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within AlertProvider");
  }
  return context;
}

/**
 * Singleton for showing alerts outside React components.
 * Must be initialized by AlertProvider.
 */
let globalShowAlert: AlertContextValue["showAlert"] | null = null;

export function setGlobalShowAlert(fn: AlertContextValue["showAlert"] | null) {
  globalShowAlert = fn;
}

/**
 * Show alert from outside React (e.g., from utility functions).
 * Falls back to native alert() if AlertProvider is not mounted.
 */
export function showAppAlert(
  message: string,
  type: AlertType = "info",
  callback?: () => void
): void {
  if (globalShowAlert) {
    globalShowAlert(message, type, callback);
  } else {
    // Fallback to native alert if context not available
    alert(message);
    callback?.();
  }
}
